/**
 * ConnectionsDialog
 *
 * A dialog (opened from the workflow overflow menu, next to Publish) that lets
 * the user view and change the connections the current workflow uses. It
 * reuses {@link TemplateConnectionsContent} — the same picker/creation flow the
 * template apply path used — but derives its requirements from the workflow's
 * live connection references instead of a template definition.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Button,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useConnectionStore } from '@/stores/connectionStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { deriveWorkflowConnectionRequirements } from '@/utils/connections';
import { TemplateConnectionsContent } from './Templates/TemplateConnectionsContent';
import type { ConnectionMapping } from '@/types/connectionReference';
import type { TemplateConnectionRequirement } from '@/types/template';

const useStyles = makeStyles({
  surface: {
    maxWidth: '640px',
    width: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '320px',
    maxHeight: '60vh',
  },
  closeButton: {
    position: 'absolute',
    right: tokens.spacingHorizontalM,
    top: tokens.spacingVerticalM,
  },
});

export interface ConnectionsDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Optional canvas node id allowlist (e.g. `['action-Foo', 'trigger-Bar']`).
   * When provided, only connection references used by these nodes are listed;
   * otherwise every connector this workflow uses is shown.
   */
  nodeIds?: string[];
}

export const ConnectionsDialog = ({ open, onClose, nodeIds }: ConnectionsDialogProps): JSX.Element => {
  const styles = useStyles();
  const references = useConnectionStore((s) => s.references);
  const mapping = useConnectionStore((s) => s.mapping);
  const setMappingFromWorkflow = useConnectionStore((s) => s.setMappingFromWorkflow);
  const workflow = useWorkflowStore((s) => s.workflow);

  // Refresh the workflow-scoped mapping when the dialog opens so it reflects any
  // edits made since load (the store mapping is the same source the canvas uses).
  useEffect(() => {
    if (open) setMappingFromWorkflow(workflow);
  }, [open, workflow, setMappingFromWorkflow]);

  // Stable signature for the optional allowlist so the memo below doesn't churn
  // when callers pass a fresh array of the same ids on every render.
  const nodeIdsKey = nodeIds ? [...nodeIds].sort().join('|') : '';

  const scopedMapping = useMemo<ConnectionMapping>(() => {
    if (!nodeIds || nodeIds.length === 0) return mapping;
    const allow = new Set(nodeIds);
    const next: ConnectionMapping = {};
    for (const id of allow) {
      const ref = mapping[id];
      if (ref) next[id] = ref;
    }
    return next;
    // nodeIds reference is intentionally excluded — nodeIdsKey covers content changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapping, nodeIdsKey]);

  const requirements = useMemo<TemplateConnectionRequirement[]>(
    () => deriveWorkflowConnectionRequirements(scopedMapping, references),
    [scopedMapping, references],
  );

  // The Accordion inside `TemplateConnectionsContent` keys each row by
  // `req.referenceKey`. When the user picks a different connection, the
  // store remap rebinds that node's reference, the derivation above
  // recomputes with the new key, and — if we passed the live list down —
  // the row's key would flip and React would unmount/remount the
  // AccordionItem, collapsing it mid-click. Snapshot the requirements when
  // the dialog opens so the row identity is stable for the dialog session;
  // chained picks track the live binding separately inside the content
  // component.
  const [snapshot, setSnapshot] = useState<TemplateConnectionRequirement[] | null>(null);
  useEffect(() => {
    // Capture on open transition, clear on close. Live `requirements` changes
    // while the dialog is open must NOT refresh the snapshot — that would
    // collapse the row the user just picked from.
    setSnapshot(open ? requirements : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const stableRequirements = useMemo(() => snapshot ?? requirements, [snapshot, requirements]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>
            Connections
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={onClose}
              className={styles.closeButton}
              aria-label="Close"
            />
          </DialogTitle>
          <DialogContent className={styles.content}>
            <TemplateConnectionsContent connectionRequirements={stableRequirements} />
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ConnectionsDialog;
